/*
 * Copyright (C) 2025 Musarubra US LLC.  All Rights Reserved.
 */

import { NonWidgetPage } from './non-widget-page-base';
import { iamLogin, iamUtils, iamErrorCodes } from '../main';
import { OktaSignIn } from '@okta/okta-signin-widget';
import { SessionObject } from '@okta/okta-auth-js';
import { iamLocalization } from '../localization';
import { iam } from '../iam';

// the okta session object returned at runtime also includes the user's login, which okta-auth-js's typings omit
type OktaSession = SessionObject & { login?: string };

export class EmailPage extends NonWidgetPage {
  public override localize(): void {
    super.localize();
    iamLocalization.addLocalization(() => {
      iamLogin.localizeElement('main-header', 'head');
      iamLogin.localizeElement('pageTitle', 'signInTitle');
      iamLogin.localizeElement('signUpText', { stringKey: 'mvisionAccountSignin', stringParameters: { company: iam.settings.branding === 'skyhigh' ? 'Skyhigh Security' : 'Trellix' } });
      iamLogin.localizeElement('signUpNow', 'signUpNow');
      iamLogin.localizeElement('signUpLink', { attribute: 'href', localeSubstitution: true, localeSeparator: '-', string: iam.settings.signUpURL });
      if (iam.settings.govCloud) {
        iamLogin.localizeElement('govCloudNotice', {
          stringKey: iam.settings.govCloudCA ? 'govCloudNoticeCA' : 'govCloudNotice',
          stringParameters: {
            startHeader: '<h5>',
            endHeader: '</h5>',
            startParagraph: '<p>',
            endParagraph: '</p>'
          },
          rawHTML: true
        });
        iamLogin.localizeElement('govCloudButton', 'ok');
      }
      iamLogin.localizeElement('sub-header', 'credentialsSubhead');
      iamLogin.localizeElement(document.querySelector('label[for=login_hint]'), { stringKey: 'email', isWidgetElement: true });
      iamLogin.localizeElement(this.elements.getSubmitButton(), { stringKey: 'next', isWidgetElement: true });
    });
  }

  public override initialize(): void {
    // do all synchronous initialization first
    iamLogin.addLangOptions();
    this.enableFields(false);
    iamLogin.showWidget();
    console.log('[Auth Code] querystrings on email.html: ', iamLogin.parseQuery(iam.Token.getLocation().search));

    if (iamLogin.storageEnabled && iam.Token.tokenExpired()) {
      iam.Token.removeItem('accessToken');
      iam.Token.removeItem('accessTokenExpires');
      window.localStorage.removeItem('tenants');
      window.localStorage.removeItem('tokenExchange');
    }

    /*
    The following code block is a special use case to handle logouts triggered by Fireeye Appliances
    During logout, next_url is passed by appliance to Authz and is validated by Authz
    Authz wraps the next_url in a custom JWT and passes to okta, and which is passed back to signin
    signin reads/validates the JWT and redirects to next_url
    TODO - remove this block when Appliances integrate with TIAM without ShimService
    */
    if (iam.Token.getLocation().hash.length > 1) {
      const params = iam.Token.getHashParams(iam.Token.getLocation().hash);
      const nxtUrlTkn = params?.next_url;

      // The nxtUrlTkn should be a JWT signed by Authz
      // In case of any error reading/validating JWT, redirection will not happen
      if (nxtUrlTkn && nxtUrlTkn.length !== 0) {
        if (iam.settings.debug) console.log('Received next_url hash param. Will attempt to decode JWT and redirect: ' + nxtUrlTkn);
        try {
          const base64Url = nxtUrlTkn.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(c => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          }).join(''));
          const decodedTkn = JSON.parse(jsonPayload);

          // The next_url token issuer should be the Authz server
          const tknIssUrl = new URL(decodedTkn.iss);
          const authSerUrl = new URL(iam.settings.authServer);

          if (tknIssUrl.hostname === authSerUrl.hostname) {
            // Validate token signature by calling Authz validation_endpoint
            this.validateToken(nxtUrlTkn, decodedTkn.iss)
              .then(res => {
                // Check if decodedTkn.next_url is a valid url
                if (iamUtils.isValidUrl(decodedTkn.next_url)) {
                  if (iam.settings.debug) console.log(`Token validation success. Will redirect to ${decodedTkn.next_url}. Response = ${res}`);
                  iam.Token.setLocation(decodedTkn.next_url);
                } else {
                  if (iam.settings.debug) console.log(`Decoded next_url ${decodedTkn.next_url}, is not a valid URL. Will not redirect`);
                }
              })
              .catch(err => {
                if (iam.settings.debug) console.log('Could not validate token signature. Will not redirect', err);
              });
          } else {
            if (iam.settings.debug) console.log(`The next_url_token token issuer ${tknIssUrl.hostname} does not match the Authz server ${authSerUrl.hostname}`);
          }
        } catch (err) {
          if (iam.settings.debug) console.log('Error reading next_url. Will not redirect', err);
        }
      }
    }

    if (iam.settings.govCloud && iam.Token.tokenExpired()) {
      document.getElementById('gov-cloud-pane').style.display = '';
      document.getElementById('page-container').style.display = 'none';
      document.getElementById('govCloudButton').addEventListener('click', () => {
        document.getElementById('gov-cloud-pane').style.display = 'none';
        document.getElementById('page-container').style.display = '';
        document.getElementById('login_hint').focus();
      });
      document.getElementById('govCloudButton').focus();
    } else {
      document.getElementById('gov-cloud-pane').style.display = 'none';
      document.getElementById('page-container').style.display = '';
    }

    if (iamLogin.storageEnabled) {
      window.localStorage.removeItem('t_id');
    }
    if (!iam.settings.blockLogin) {
      this.isOktaSessionExists().then(async (oktaSession: SessionObject) => {
        // Clear the local session if Okta session is not active
        if (oktaSession && oktaSession.status !== 'ACTIVE' && iamLogin.storageEnabled) {
          iam.Token.removeItem('accessToken');
          iam.Token.removeItem('accessTokenExpires');
          window.localStorage.removeItem('tenants');
          window.localStorage.removeItem('tokenExchange');
          await iam.Token.removeSessionCookie(); // IAM-7040 - kill session cookie on Dashboard page from Skyhigh product logout, triggering logout on other apps
        }
        const isAuthCodeRequest = !!iamLogin.parseQuery(iam.Token.getLocation().search).SAMLRequest;
        this.setEmailFormAction(oktaSession, isAuthCodeRequest).then(() => {
          // now do asynchronous
          iamLogin.handleErrors();
          if (!iamLogin.storageEnabled) {
            (document.getElementById('login_hint') as HTMLButtonElement).disabled = true;
          }
          this.initializeEmailPseudoWidget();
        });
      });
    } else {
      this.initializeEmailPseudoWidget();
      document.getElementById('loading-content').style.display = 'none';
      document.getElementById('right-pane-contents').style.display = '';
      (document.getElementById('login_hint') as HTMLButtonElement).disabled = true;
    }
  }

  private validateToken(token: string, issuer: string) {
    return new Promise(function (resolve, reject) {
      const xhr = new XMLHttpRequest();
      xhr.onreadystatechange = function () {
        if (xhr.readyState === XMLHttpRequest.DONE) {
          if (xhr.status === 200) {
            try {
              if (iam.settings.debug) console.log('validateToken api response=' + xhr.responseText);
              resolve(JSON.parse(xhr.responseText));
            } catch (error) {
              console.log('validateToken api error=' + error);
              reject(error);
            }
          } else {
            console.log('validateToken api status error=' + xhr.status);
            reject(xhr.responseText);
          }
        }
      };

      // Construct the validation_endpoint from issuer and then invoke validation_endpoint
      xhr.open('POST', issuer + '/validate', true);
      xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
      xhr.send(`token=${token}`);
    });
  }

  private initializeEmailPseudoWidget() {
    // email.html page (which doesn't use widget, hence "pseudo" widget)
    if (iam.settings.debug) console.log('initializing email widget');
    document.getElementById('login_hint').focus();
    this.showCommonFields([]);
    this.initializeCommon();
    this.initializePseudoWidgetCommon();
  }

  public override validateForm() {
    // disable submit button if the value isn't a valid email address
    this.elements.getSubmitButton().disabled = !iamLogin.isEmailAddressValid(iamLogin.getEmailFormValue());
  }

  public getIdpUrl(tenantId: string): Promise<string | false> {
    return new Promise((resolve, reject) => {
      if (!tenantId) {
        resolve(false);
      } else {
        const xmlhttp = new XMLHttpRequest();
        xmlhttp.onreadystatechange = () => {
          if (xmlhttp.readyState === XMLHttpRequest.DONE) {
            if (xmlhttp.status === 200) {
              if (iam.settings.debug) console.log('XMLHttpRequest', xmlhttp);
              if (xmlhttp.responseText) {
                resolve(JSON.parse(xmlhttp.responseText));
              }
            } else if (xmlhttp.status === 404) {
              resolve(false);
            } else {
              reject(new Error(xmlhttp.responseText));
            }
          }
        };

        xmlhttp.open('GET', iam.settings.idpUrl + tenantId, true);
        xmlhttp.send();
      }
    });
  }

  public setEmailFormSubmit() {
    // Get the language selection and stored in local storage and in cookie
    const locale = document.getElementById('selectLanguage').dataset.value;
    iamLogin.setEmailFormValue(iamLogin.getEmailFormValue().trim());
    if (!iamLogin.isEmailAddressValid(iamLogin.getEmailFormValue())) {
      iamLogin.showErrorMessageLocalizable('emailAddressValid');
      return false;
    }
    window.localStorage.setItem('login_hint', iamLogin.getEmailFormValue());
    if (iam.settings.usePKCE) window.sessionStorage.setItem('pkce_completion_redirect', iam.settings.tokenHandler);
    if (locale) {
      iamLogin.setCookie('iam_user_locale', locale);
      this.addFormField('locale', locale);
    }
    if (!iam.settings.usePKCE && !(document.getElementById('scope') as HTMLInputElement).value) {
      iamLogin.showUnexpectedErrorMessage(iamErrorCodes.loadingScopes);
      return false;
    }

    iamLogin.hideMessageBanners();
    return true;
  }

  private addFormField(name: string, value: string) { // jshint ignore:line
    if (document.getElementById(name)) document.getElementById(name).parentElement.removeChild(document.getElementById(name));
    const input = document.createElement('input');
    input.setAttribute('type', 'hidden');
    input.setAttribute('name', name);
    input.setAttribute('id', name);
    input.setAttribute('value', value);
    document.getElementById('emailForm').appendChild(input);
  }

  public enableFields(enable?: boolean) {
    if (arguments.length === 0) {
      enable = true;
    }
    (document.getElementById('login_hint') as HTMLInputElement).disabled = !enable;
    this.elements.getSubmitButton().disabled = !enable;
  }
  
  // sets the email form fields and action for initial email.html page
  public setEmailFormAction(oktaSession: OktaSession, isAuthCodeRequest: boolean) {
    console.log(oktaSession);
    // todo: when we remove Implicit flow, remove getScopes too. No longer needed
    return iam.Token.getScopes().then(async scope => {
      const errorInURL = iamLogin.parseQuery(iam.Token.getLocation().search).error || iam.Token.getHashParams(iam.Token.getLocation().hash).error || iamLogin.parseQuery(iam.Token.getLocation().search).error_description || iam.Token.getHashParams(iam.Token.getLocation().hash).error_description;

      // Fix for continuous loop when an non-mvision user tries to reach mvision after successful login
      if (!errorInURL && iamLogin.storageEnabled && iam.Token.getItem('accessToken') && !iam.Token.tokenExpired() &&
        document.referrer && document.referrer.indexOf(iam.settings.tokenHandler) !== -1) {
        iam.Token.setLocation(iam.settings.appURL);
        return scope;
      }

      if (iamLogin.storageEnabled) {
        iamLogin.setEmailFormValue(window.localStorage.getItem('login_hint'));
      }
      document.forms.namedItem('emailForm').action = iam.settings.usePKCE ? iam.settings.pkceAuthURL : iam.settings.redirectUrl;

      document.forms.namedItem('emailForm').onsubmit = event => {
        if (iam.settings.debug) console.log('Submitting form');
        if (iamLogin.sessionIsActive()) {
          iamLogin.showTransitioningWidgetPage();
          iam.Token.setLocation(iam.settings.alreadySignedIn + '?error=' + encodeURIComponent(iamLocalization.localize('alreadyLoggedInError')));
          event.stopImmediatePropagation(); // since the submit won't take place, prevent other submit handlers from running, e.g. don't call hideMessageBanners()
          return false;
        } else if (this.setEmailFormSubmit() && isAuthCodeRequest) { // authcode block
          iamLogin.showTransitioningWidgetPage();
          iam.Token.setLocation(iam.settings.signInPage + '/login.html?SAMLRequest=' + encodeURIComponent(iamLogin.parseQuery(iam.Token.getLocation().search).SAMLRequest) + '&RelayState=' + encodeURIComponent(iamLogin.parseQuery(iam.Token.getLocation().search).RelayState) + '&SigAlg=' + encodeURIComponent(iamLogin.parseQuery(iam.Token.getLocation().search).SigAlg) + '&Signature=' + encodeURIComponent(iamLogin.parseQuery(iam.Token.getLocation().search).Signature) + '&no_login_hint=true');
          event.stopImmediatePropagation();
          return false;
        } else if (this.setEmailFormSubmit()) {
          iamLogin.showTransitioningWidgetPage();
          this.elements.getSubmitButton().disabled = true;
          iamLogin.registerLocalizationCallback(() => {
            iamLogin.localizeElement('sub-header', 'verifyingSubhead');
          });
          return true;
        } else {
          event.stopImmediatePropagation(); // since the submit won't take place, prevent other submit handlers from running, e.g. don't call hideMessageBanners()
          return false;
        }
      };
      const redirectUri = this.getRedirectURI();
      if (redirectUri && iamLogin.storageEnabled) {
        if (iam.settings.debug) console.log('Setting redirect_uri for app to: ' + redirectUri);
        window.sessionStorage.setItem('redirect_uri', redirectUri);
      }

      if (document.referrer.indexOf(iam.settings.domain) === 0) {
        iamLogin.showInfoMessageLocalizable('logoutMessage');
      }

      // When coming from PBC portal, tId is passed as a query parameter and needs to be stored in cookie/local storage
      const tId = iamLogin.parseQuery(iam.Token.getLocation().search).t_id;
      if (tId && iamLogin.storageEnabled) {
        window.localStorage.setItem(iam.consts?.hashKeys.tId, tId);
        iamLogin.setCookie(iam.consts?.storageKeys.tId, window.localStorage.getItem(iam.consts?.hashKeys.tId));
      }
      else {
        // Clear any stale tId from storage and cookie before a new session
        if (!errorInURL && iamLogin.storageEnabled &&
            !(iam.Token.getItem('accessToken') && !iam.Token.tokenExpired())) {
          console.log('Clearing stale tId from storage and cookie');
          window.localStorage.removeItem(iam.consts?.hashKeys.tId);
          iamLogin.removeCookie(iam.consts?.storageKeys.tId);
        }
      }

      let initiator;
      if (iam.settings.usePKCE) {
        initiator = window.sessionStorage.getItem('initiator') || iamLogin.parseQuery(iam.Token.getLocation().search).initiator;
      } else {
        initiator = iamLogin.parseQuery(iam.Token.getLocation().search).initiator;
      }
      if (initiator && iamLogin.storageEnabled && window.sessionStorage.getItem('redirect_uri')) {
        window.sessionStorage.setItem('initiator', initiator);
      }

      if (await this.overlappingSession(oktaSession)) {
        iam.Token.setLocation(iam.settings.alreadySignedIn);
        return scope;
      }

      // is storageEnabled ever true in PKCE? Does this block get invoked?
      if (iamLogin.storageEnabled && iam.Token.getItem('accessToken') && !iam.Token.tokenExpired() && !errorInURL) {
        // if not pkce, do setLocation, if pkce, redir to dashboard
        if (!iam.settings.usePKCE) {
          // IMPLICIT
          iam.Token.setLocation(iam.settings.redirectUrl +
            '?redirect_uri=' + iam.settings.tokenHandler +
            '&client_id=' + iam.settings.clientID +
            '&response_type=token' +
            '&scope=' + scope +
            '&partial=1' +
            '&locale=' + iamLocalization.getLocale()
          );
        } else {
          // PKCE
          iam.Token.setLocation(redirectUri); // send to redirectUri
        }
        return scope;
      }
      if (!errorInURL && oktaSession && oktaSession.status === 'ACTIVE') {
        // If we have an okta session, but no access token, go ahead and forward to authorization server to get token.
        console.log('Logged into Okta, forwarding');
        if (!iam.settings.usePKCE) {
          // IMPLICIT
          iam.Token.setLocation(iam.settings.redirectUrl +
            '?redirect_uri=' + iam.settings.tokenHandler +
            '&client_id=' + iam.settings.clientID +
            '&response_type=token' +
            '&scope=' + scope +
            '&partial=1' +
            (iamLogin.storageEnabled ? '&locale=' + iamLocalization.getLocale() : '') +
            '&login_hint=' + encodeURIComponent(oktaSession.login));
        } else {
          // PKCE
          iam.Token.setupNavigatorLock(async () => {
            const codeVerifier = iam.Token.generateRandomString();
            const codeChallenge = await iam.Token.pkceChallengeFromVerifier(codeVerifier);
            window.sessionStorage.setItem('pkce_code_verifier', codeVerifier);
            window.location.href = `${iam.settings.pkceAuthURL}` +
              '?response_type=code' +
              `&client_id=${iam.settings.pkceClient}` +
              `&redirect_uri=${encodeURIComponent(iam.settings.tokenHandler)}` +
              `&code_challenge=${codeChallenge}` + // eslint-disable-line
              '&code_challenge_method=S256' +
              '&login_hint=' + encodeURIComponent(oktaSession.login);
            iam.Token.pkceRedirectionInProgress = true;
          });
        }
      } else {
        this.enableFields(true);
      }

      if (iam.settings.usePKCE) {
        // PKCE
        let codeChallenge;
        if (!isAuthCodeRequest) {
          const codeVerifier = iam.Token.generateRandomString();
          codeChallenge = await iam.Token.pkceChallengeFromVerifier(codeVerifier);
          window.sessionStorage.setItem('pkce_code_verifier', codeVerifier);
        }
        this.addFormField('client_id', iam.settings.pkceClient);
        this.addFormField('redirect_uri', iam.settings.tokenHandler);
        this.addFormField('response_type', 'code');
        this.addFormField('code_challenge', codeChallenge);
        this.addFormField('code_challenge_method', 'S256');
      } else {
        // IMPLICIT
        this.addFormField('client_id', iam.settings.clientID);
        this.addFormField('redirect_uri', iam.settings.tokenHandler);
        this.addFormField('response_type', 'token');
        this.addFormField('scope', scope);
        this.addFormField('partial', '1');
      }
      document.getElementById('emailForm').style.visibility = '';

      const loginHint = iamLogin.parseQuery(iam.Token.getLocation().search).login_hint;
      const tenantId = iamLogin.parseQuery(iam.Token.getLocation().search).tenant_id;
      let locale = iamLogin.parseQuery(iam.Token.getLocation().search).locale;
      if (tenantId) {
        // if tenantId is available, add it to the form!
        this.addFormField('tenant_id', tenantId);
      }
      if (loginHint && tenantId && locale && tId) {
        iamLogin.setEmailFormValue(loginHint);
        let localeMatchFound = false;
        const languageTags = document.querySelectorAll<HTMLElement>('#selectLanguage a');
        for (let i = 0; i < languageTags.length; i++) {
          if (languageTags[i].dataset.value.toLowerCase() === locale.replace('-', '_').toLowerCase()) {
            localeMatchFound = true;
          }
        }

        if (!localeMatchFound) {
          if (iam.settings.debug) console.log('specified locale not supported: ', locale);
          locale = iam.settings.languageOptions[0].langCode;
        }

        document.getElementById('selectLanguage').dataset.value = locale;
        this.addFormField('locale', locale);
        if (this.setEmailFormSubmit()) {
          (document.getElementById('emailForm') as HTMLFormElement).submit();
        }
        return scope;
      } else {
        this.addFormField('locale', iamLogin.storageEnabled ? iamLocalization.getLocale() : '');
        document.getElementById('loading-content').style.display = 'none';
        document.getElementById('right-pane-contents').style.display = '';
        if ((!initiator || (initiator && ['mvisionsso', 'eur'].includes(initiator))) && tenantId) {
          return this.getIdpUrl(tenantId).then((idpUrl: string | false) => {
            if (idpUrl) {
              if (iamLogin.storageEnabled && window.sessionStorage.getItem('redirect_uri')) {
                window.sessionStorage.setItem('persistent_redirect_uri', window.sessionStorage.getItem('redirect_uri'));
                if (!initiator) {
                  window.sessionStorage.setItem('initiator', 'eur');
                }
              }
              idpUrl += (idpUrl.indexOf('?') >= 0 ? '&' : '?') + 'RelayState=' + iam.settings.signInPage;
              if (iam.settings.debug) console.log('Forwarding to IdP: ' + idpUrl);
              iam.Token.setLocation(idpUrl);
            }
            return scope;
          });
        } else {
          return scope;
        }
      }
    }).catch(err => {
      console.error('Failed to load scopes from: ' + iam.settings.scopeJson, err, err.message, err.stack);
      iamLogin.showUnexpectedErrorMessage(iamErrorCodes.possibleScopes);
      document.getElementById('emailForm').style.visibility = '';
      document.getElementById('loading-content').style.display = 'none';
      document.getElementById('right-pane-contents').style.display = '';
    });
  }

  public isOktaSessionExists(): Promise<SessionObject> {
    const errorInURL = iamLogin.parseQuery(iam.Token.getLocation().search).error || iam.Token.getHashParams(iam.Token.getLocation().hash).error || iamLogin.parseQuery(iam.Token.getLocation().search).error_description || iam.Token.getHashParams(iam.Token.getLocation().hash).error_description;

    return new Promise((resolve, reject) => {
      if (!errorInURL) {
        const oktaSignIn = new OktaSignIn({ baseUrl: iam.settings.domain });
        oktaSignIn.authClient.session.get().then(res => {
          resolve(res);
        }).catch(error => {
          console.error(error);
          resolve(null);
        });
      } else {
        resolve(null);
      }
    });
  }

  private getRedirectURI() {
    let redirectUri = iam.settings.customPostLoginRedirect || iam.settings.appURL;

    if (iam.Token.getLocation().search && iamLogin.parseQuery(iam.Token.getLocation().search).redirect_uri) {
      const uri = iamLogin.parseQuery(iam.Token.getLocation().search).redirect_uri;
      if (iam.settings.debug) console.log('redirect_uri: ', uri);
      redirectUri = uri;
    } else if (iam.Token.getLocation().hash && iam.Token.getHashParams(iam.Token.getLocation().hash).redirect_uri) {
      const uri = iam.Token.getHashParams(iam.Token.getLocation().hash).redirect_uri;
      if (iam.settings.debug) console.log('redirect_uri: ', uri);
      redirectUri = uri;
    } else if (iamLogin.storageEnabled && window.sessionStorage.getItem('persistent_redirect_uri')) {
      const uri = window.sessionStorage.getItem('persistent_redirect_uri');
      if (iam.settings.debug) console.log('persistent_redirect_uri' + ': ', uri);
      redirectUri = uri;
    }

    return redirectUri;
  }

  private async overlappingSession(oktaSession: OktaSession): Promise<boolean> {
    // check for any indication of overlapping sessions (trying to start another session
    // with a different user or tenant, like if you sign in directly and then via idp)
    const sessionLogins = new Set<string>();
    const sessionTenantIds = new Set<string>();
    let sessionCookie = await iam.Token.getSessionCookie();
    if (sessionCookie) {
      sessionLogins.add(sessionCookie.login_hint);
      sessionTenantIds.add(sessionCookie.tenant_id);
    }
    const token = iam.Token.getDecodedToken();
    if (token && !iam.Token.tokenExpired()) {
      sessionLogins.add(token.login_hint);
      sessionTenantIds.add(token.tenant_id);
    }
    if (oktaSession && oktaSession.status === 'ACTIVE') {
      sessionLogins.add(oktaSession.login);
      // okta session doesn't include tenant info
    }
    // login info can come on the url, like when coming from pbc
    const queryParams = iamLogin.parseQuery(iam.Token.getLocation().search);
    if (queryParams.login_hint) {
      sessionLogins.add(queryParams.login_hint);
    }
    if (queryParams.tenant_id) {
      sessionTenantIds.add(queryParams.tenant_id);
    }
    return sessionLogins.size > 1 || sessionTenantIds.size > 1;
  }
}
